/**
 * CoverCropModal — CORE-583 step 3 «Обложка».
 *
 * The step used to upload the picked file as-is: a square 600×600 PNG landed
 * straight in the horizontal cover slot, object-fit cropped it to the centre,
 * and the master had no say in which part survived. QA failed the step on
 * exactly that — the scope asks for «загрузка, кроп, замена, удаление».
 *
 * So the file now goes through a crop confirmation first. The frame is fixed at
 * the cover's own 16:9 and the image pans/zooms behind it, rather than a
 * free-drag rectangle: a fixed frame cannot produce an off-aspect cover, so the
 * output always matches the slot it is going into.
 *
 * Web-only, like the upload it feeds — the whole `StepCover` file input is
 * already behind `Platform.OS === 'web'` (a native picker would crop on-device
 * instead). Nothing here is imported on native paths.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, PanResponder, ActivityIndicator, StyleSheet } from 'react-native';
import ReactDOM from 'react-dom';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../context/ThemeContext';
import {
    COVER_ASPECT, FRAME_W, OUT_W, OUT_H, frameHeightFor,
    coverFitScale, clampOffset, cropSourceRect, zoomAnchoredOffset,
} from './coverCropGeometry';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../../styles/accentText';
const MAX_ZOOM = 3;

interface Props {
    file: File;
    onCancel: () => void;
    onApply: (cropped: File) => void;
}


export const CoverCropModal: React.FC<Props> = ({ file, onCancel, onApply }) => {
    const { colors } = useTheme();
    const [img, setImg] = useState<HTMLImageElement | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [busy, setBusy] = useState(false);
    // CORE-739 — the frame's real on-screen size, measured rather than assumed.
    // It is `maxWidth: FRAME_W` inside a sheet that is itself `maxWidth: '100%'`,
    // so on a narrow window it is narrower than FRAME_W. The geometry used the
    // constants regardless, which made the crop disagree with the preview at
    // exactly the widths where the master could least afford it.
    const [frameW, setFrameW] = useState(FRAME_W);
    const frameH = frameHeightFor(frameW);

    // Live refs for the pan gesture: PanResponder handlers are created once, so
    // reading state through them would capture the first render's values.
    const offsetRef = useRef(offset);
    offsetRef.current = offset;
    const startRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const url = URL.createObjectURL(file);
        const el = new window.Image();
        el.onload = () => setImg(el);
        el.onerror = () => setLoadError('Не удалось открыть изображение');
        el.src = url;
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const baseScale = useMemo(
        () => (img ? coverFitScale(img.naturalWidth, img.naturalHeight, frameW, frameH) : 1),
        [img, frameW, frameH],
    );

    const scale = baseScale * zoom;
    const dispW = img ? img.naturalWidth * scale : 0;
    const dispH = img ? img.naturalHeight * scale : 0;

    const clamp = useCallback(
        (o: { x: number; y: number }, w: number, h: number) => clampOffset(o, w, h, frameW, frameH),
        [frameW, frameH],
    );

    // Centre on load and re-clamp on zoom, anchoring the frame's midpoint so
    // zooming pulls toward what the master is already looking at.
    // Re-centres on a frame resize too: the offsets are in frame pixels, so a
    // resized frame would otherwise hold a stale, off-by-the-delta selection.
    useEffect(() => {
        if (!img) return;
        setOffset({ x: (frameW - dispW) / 2, y: (frameH - dispH) / 2 });
        // Only on a new image or a new frame size — zoom is handled below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [img, frameW, frameH]);

    const onZoom = (next: number) => {
        const z = Math.min(MAX_ZOOM, Math.max(1, next));
        if (!img) { setZoom(z); return; }
        const prevScale = baseScale * zoom;
        const nextScale = baseScale * z;
        const raw = zoomAnchoredOffset(offsetRef.current, prevScale, nextScale, frameW, frameH);
        setZoom(z);
        setOffset(clamp(raw, img.naturalWidth * nextScale, img.naturalHeight * nextScale));
    };

    const pan = useMemo(() => PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: () => { startRef.current = { ...offsetRef.current }; },
        onPanResponderMove: (_e, g) => {
            setOffset(clamp({ x: startRef.current.x + g.dx, y: startRef.current.y + g.dy }, dispW, dispH));
        },
    }), [clamp, dispW, dispH]);

    const apply = async () => {
        if (!img || busy) return;
        setBusy(true);
        try {
            const { sx, sy, sw, sh } = cropSourceRect(offset, scale, frameW, frameH);

            const canvas = document.createElement('canvas');
            canvas.width = OUT_W;
            canvas.height = OUT_H;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('canvas unavailable');
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);

            // PNG only when the source was PNG — a photographic JPEG re-encoded
            // as PNG balloons for no visual gain.
            const png = file.type === 'image/png';
            const mime = png ? 'image/png' : 'image/jpeg';
            const blob: Blob | null = await new Promise((res) =>
                canvas.toBlob((b) => res(b), mime, png ? undefined : 0.9));
            if (!blob) throw new Error('crop failed');

            const base = (file.name || 'cover').replace(/\.[^.]+$/, '');
            onApply(new File([blob], `${base}.${png ? 'png' : 'jpg'}`, { type: mime }));
        } catch {
            setLoadError('Не удалось обрезать изображение');
            setBusy(false);
        }
    };

    // Portal to <body>, same reason as `chat/MemoryModal`: rendered in place the
    // sheet is trapped in the wizard step's stacking context, and the wizard's
    // full-width footer row paints after it. The footer is transparent between
    // «Назад» and «Продолжить», so the sheet still *looked* right while that
    // invisible strip swallowed every click on «Применить». Caught by clicking
    // it in a browser — nothing about the render gives it away.
    const sheet = (
        <View style={styles.backdrop}>
            <View style={[styles.sheet, { backgroundColor: colors.surfaceElevated, borderColor: colors.border }]}>
                <Text style={[styles.title, { color: colors.textPrimary }]}>Кадрирование обложки</Text>
                <Text style={[styles.sub, { color: colors.textMuted }]}>
                    Перетащите изображение и приблизьте. Рамка — это и есть карточка: что видно
                    здесь, то и попадёт в анонс.
                </Text>

                <View
                    style={[styles.frame, { borderColor: colors.solar, backgroundColor: colors.canvas }]}
                    onLayout={(e) => {
                        const w = e.nativeEvent.layout.width;
                        if (w > 0 && Math.abs(w - frameW) > 0.5) setFrameW(w);
                    }}
                    {...pan.panHandlers}
                >
                    {img ? (
                        React.createElement('img' as any, {
                            src: img.src,
                            draggable: false,
                            style: {
                                position: 'absolute',
                                left: offset.x, top: offset.y,
                                width: dispW, height: dispH,
                                maxWidth: 'none', userSelect: 'none', cursor: 'grab',
                            },
                        })
                    ) : (
                        <View style={styles.center}>
                            {loadError
                                ? <Text style={{ color: colors.error, fontSize: 13 }}>{loadError}</Text>
                                : <ActivityIndicator color={ACCENT_TEXT} />}
                        </View>
                    )}
                </View>

                <View style={styles.zoomRow}>
                    <Ionicons name="remove-outline" size={18} color={colors.textMuted} />
                    {React.createElement('input' as any, {
                        type: 'range', min: 1, max: MAX_ZOOM, step: 0.01, value: zoom,
                        disabled: !img,
                        onChange: (e: any) => onZoom(Number(e.target.value)),
                        style: { flex: 1, accentColor: colors.solar, cursor: 'pointer' },
                    })}
                    <Ionicons name="add-outline" size={18} color={colors.textMuted} />
                </View>

                {loadError && img && <Text style={{ color: colors.error, fontSize: 13 }}>{loadError}</Text>}

                <View style={styles.actions}>
                    <Pressable
                        onPress={onCancel}
                        style={[styles.btn, { borderColor: colors.border }, { cursor: 'pointer' } as any]}
                    >
                        <Text style={{ color: colors.textMuted, fontWeight: '600', fontSize: 14 }}>Отмена</Text>
                    </Pressable>
                    <Pressable
                        onPress={apply}
                        disabled={!img || busy}
                        style={[
                            styles.btn,
                            { borderColor: colors.solar, backgroundColor: colors.solar, opacity: !img || busy ? 0.6 : 1 },
                            { cursor: 'pointer' } as any,
                        ]}
                    >
                        {busy
                            ? <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                            : <Text style={{ color: ON_ACCENT_TEXT, fontWeight: '700', fontSize: 14 }}>Применить</Text>}
                    </Pressable>
                </View>
            </View>
        </View>
    );

    return ReactDOM.createPortal(sheet, document.body);
};

const styles = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        position: 'fixed' as any,
        backgroundColor: 'rgba(0,0,0,0.55)',
        alignItems: 'center', justifyContent: 'center',
        // CORE-1171 — кроп зовут и из формы правки сессии, а та живёт в RN
        // `<Modal>`: react-native-web кладёт его слоем 9999, и при 1000 лист
        // кадрирования оказывался ПОД ним — виден, но недоступен. Портал в
        // body уже вынимает лист из любого родителя, не хватало только слоя.
        zIndex: 10000, padding: 20,
    },
    sheet: { width: FRAME_W + 48, maxWidth: '100%', borderWidth: 1, borderRadius: 18, padding: 24 },
    title: { fontSize: 18, fontWeight: '700' },
    sub: { fontSize: 13, marginTop: 6, marginBottom: 16, lineHeight: 19 },
    frame: {
        // aspectRatio, not a fixed height: the frame is allowed to shrink with
        // the sheet, and it has to stay 16:9 while it does — the crop maths is
        // fed the measured size, and both must agree with the card (CORE-739).
        width: '100%', maxWidth: FRAME_W, aspectRatio: COVER_ASPECT,
        alignSelf: 'center',
        borderRadius: 12, borderWidth: 2, overflow: 'hidden', position: 'relative',
    },
    center: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
    zoomRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 16 },
    actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 20 },
    btn: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        minWidth: 120, borderWidth: 1.5, borderRadius: 12, paddingHorizontal: 18, paddingVertical: 11,
    },
});

export default CoverCropModal;
